import "core-js/modules/web.dom-collections.iterator.js";
import { PureComponent } from 'react';
import styled from 'styled-components';
import { unit } from '../../../styles/unit';
import { Portal } from '../../Portal/Portal';
import { animationStyle } from '../lib/toastAnimationStyle';
import { toastManager } from '../lib/toastManager';
import { positionStyle } from '../lib/toastPositionStyle';
import { Toast } from './Toast';
import { jsx as _jsx } from "react/jsx-runtime";

const removeAtIndex = (list, index) => [...list.slice(0, index), ...list.slice(index + 1)];

const getToastsHighestZIndex = toasts => {
  const zIndexes = toasts.map(toast => toast.options.zIndex).filter(Boolean);
  return zIndexes.length ? Math.max(...zIndexes) : undefined;
};

const ToastsContainer = /*#__PURE__*/styled.div.withConfig({
  displayName: "ToastsContainer",
  componentId: "sc-1t53icn-0"
})(["position:fixed;display:flex;flex-direction:column;z-index:", ";animation:", ";left:", ";top:", ";right:", ";bottom:", ";transform:", ";>:not(:first-child){margin-top:", ";}"], props => props.zIndex || '9999999', props => animationStyle(props.position), props => positionStyle(props.position)[0], props => positionStyle(props.position)[1], props => positionStyle(props.position)[2], props => positionStyle(props.position)[3], props => positionStyle(props.position)[4], unit(2));

class Toaster extends PureComponent {
  constructor(...args) {
    super(...args);
    this.state = {
      toasts: []
    };
    this.closing = {};

    this.makeToast = toast => {
      if (toast.options.duration) {
        this.closeToastIn(toast.id, toast.options.duration);
      }

      this.setState(previousState => ({
        toasts: [...previousState.toasts, toast]
      }));
    };

    this.closeToastIn = (toastId, duration = 0) => {
      this.closing[toastId] = setTimeout(() => {
        this.closeToast(toastId);
      }, duration);
    };

    this.closeToast = toastId => {
      if (this.closing[toastId]) {
        clearTimeout(this.closing[toastId]);
        delete this.closing[toastId];
      }

      const {
        toasts
      } = this.state;
      const toastIndex = toasts.findIndex(toast => toast.id === toastId);

      if (toastIndex === -1) {
        return;
      }

      this.setState(previousState => ({
        toasts: removeAtIndex(previousState.toasts, toastIndex)
      }));
    };

    this.clearClosing = () => {
      Object.values(this.closing).forEach(clearTimeout);
      this.closing = {};
    };

    this.closeAllToasts = () => {
      this.clearClosing();
      this.setState(() => ({
        toasts: []
      }));
    };
  }

  componentDidMount() {
    toastManager.setup({
      onMake: this.makeToast,
      onClose: this.closeToast,
      onCloseAll: this.closeAllToasts
    });
  }

  componentWillUnmount() {
    if (toastManager.isStarted()) {
      this.clearClosing();
      toastManager.stop();
    }
  }

  render() {
    if (!toastManager.isStarted()) {
      return null;
    }

    const {
      toasts
    } = this.state;
    const positionGroups = toasts.reduce((memo, toast) => {
      const {
        position
      } = toast.options;

      if (!memo[position]) {
        memo[position] = []; // eslint-disable-line no-param-reassign
      }

      memo[position].push(toast);
      return memo;
    }, {});
    const containers = Object.keys(positionGroups).map(position => {
      const positionedToasts = positionGroups[position];
      const zIndex = getToastsHighestZIndex(positionedToasts);
      return /*#__PURE__*/_jsx(ToastsContainer, Object.assign({}, this.props, {
        position: position,
        zIndex: zIndex,
        children: positionedToasts.map(({
          id,
          content,
          options
        }) => /*#__PURE__*/_jsx(Toast, Object.assign({
          id: id
        }, options, {
          children: typeof content === 'function' ? content(id, options) : content
        }), id))
      }), position);
    });
    return containers.length ? /*#__PURE__*/_jsx(Portal, {
      children: containers
    }) : null;
  }

}

export { Toaster };